import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Snackbar,
} from '@mui/material';
import {
  Logout,
  Visibility,
  Search,
  Refresh,
  FilterList,
  MoreVert,
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import { getFormSubmissions, updateSubmissionStatus } from '../../services/nocodbService';
import AppTheme from '../../frontend/shared-theme/AppTheme';

interface Submission {
  Id: string;
  business_name: string;
  submission_date: string;
  status: string;
  business_industry: string;
}

const statusOptions = ['pending', 'in_progress', 'approved', 'completed', 'rejected'];

const DashboardPage: React.FC = () => {
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSubmissionId, setSelectedSubmissionId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const { logout, user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getFormSubmissions(page + 1, rowsPerPage);
        setSubmissions(response.list || []);
        setTotalRows(response.pageInfo.totalRows || 0);
      } catch (err: any) {
        setError(err.message || 'Erro ao carregar submissões');
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [page, rowsPerPage, refreshKey]);

  const handleFilterMenuOpen = (event: React.MouseEvent<HTMLElement>) => setFilterAnchorEl(event.currentTarget);
  const handleFilterMenuClose = () => setFilterAnchorEl(null);

  const handleStatusFilterSelect = (status: string) => {
    setPage(0);
    setFilterStatus(status);
    handleFilterMenuClose();
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowMenuOpen = (event: React.MouseEvent<HTMLElement>, submissionId: string) => {
    setRowMenuAnchorEl(event.currentTarget);
    setSelectedSubmissionId(submissionId);
  };

  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
    setSelectedSubmissionId(null);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!selectedSubmissionId) return;
    try {
      await updateSubmissionStatus(selectedSubmissionId, newStatus);
      setSubmissions(prev => prev.map(sub => sub.Id === selectedSubmissionId ? { ...sub, status: newStatus } : sub));
      setSnackbar({ open: true, message: 'Status atualizado com sucesso!', severity: 'success' });
    } catch (err: any) {
      setSnackbar({ open: true, message: `Falha ao atualizar status: ${err.message}`, severity: 'error' });
    } finally {
      handleRowMenuClose();
    }
  };

  const filteredSubmissions = submissions.filter(sub => {
    const statusMatch = !filterStatus || (sub.status && sub.status.toLowerCase() === filterStatus.toLowerCase());
    const searchMatch = searchTerm === '' ||
      (sub.business_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (sub.business_industry || '').toLowerCase().includes(searchTerm.toLowerCase());
    return statusMatch && searchMatch;
  });

  // --- CORREÇÃO: Implementação da função restaurada ---
  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      }).format(date);
    } catch {
      return dateString;
    }
  };

  // --- CORREÇÃO: Implementação da função restaurada ---
  const renderStatusChip = (status: string): React.ReactNode => {
    let color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 'default';
    switch (status?.toLowerCase()) {
      case 'pending': color = 'warning'; break;
      case 'in_progress': color = 'info'; break;
      case 'approved': color = 'success'; break;
      case 'rejected': color = 'error'; break;
      case 'completed': color = 'primary'; break;
    }
    return <Chip label={status || 'N/A'} color={color} size="small" variant="outlined" />;
  };

  return (
    <AppTheme>
      <Box sx={{ flexGrow: 1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <AppBar position="static">{/* ... AppBar ... */}</AppBar>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flexGrow: 1 }}>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
              <Typography variant="h5" component="h1" color="primary" sx={{ fontWeight: 600 }}>Submissões de Formulário</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button startIcon={<Refresh />} onClick={() => setRefreshKey(prev => prev + 1)} variant="outlined" size={isMobile ? 'small' : 'medium'}>Atualizar</Button>
                <Button startIcon={<FilterList />} variant="outlined" size={isMobile ? 'small' : 'medium'} onClick={handleFilterMenuOpen} sx={{ display: { xs: 'none', sm: 'flex' } }}>Filtrar {filterStatus && `(${filterStatus})`}</Button>
              </Box>
            </Box>

            <Menu anchorEl={filterAnchorEl} open={Boolean(filterAnchorEl)} onClose={handleFilterMenuClose}>
              <MenuItem onClick={() => handleStatusFilterSelect('')}><em>Todos os Status</em></MenuItem>
              {statusOptions.map((status) => (<MenuItem key={status} onClick={() => handleStatusFilterSelect(status)}>{status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>))}
            </Menu>

            <Box sx={{ mb: 3 }}>
              <TextField fullWidth placeholder="Buscar por nome, indústria ou status..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} InputProps={{ startAdornment: (<InputAdornment position="start"><Search color="action" /></InputAdornment>)}} variant="outlined" size="small" />
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? ( <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box>
            ) : filteredSubmissions.length === 0 ? (
              <Box sx={{ py: 5, textAlign: 'center' }}><Typography variant="body1" color="text.secondary">Nenhuma submissão encontrada.</Typography></Box>
            ) : (
              <>
                <TableContainer>
                  <Table aria-label="tabela de submissões">
                    <TableHead>
                      <TableRow>
                        <TableCell>Empresa</TableCell>
                        <TableCell>Indústria</TableCell>
                        <TableCell>Data</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Ações</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSubmissions.map((submission) => (
                        <TableRow key={submission.Id} hover>
                          <TableCell component="th" scope="row">{submission.business_name}</TableCell>
                          <TableCell>{submission.business_industry}</TableCell>
                          <TableCell>{formatDate(submission.submission_date)}</TableCell>
                          <TableCell>{renderStatusChip(submission.status)}</TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                              <Button startIcon={<Visibility />} size="small" component={Link} to={`/admin/submissions/${submission.Id}`} variant="text">
                                {isMobile ? '' : 'Detalhes'}
                              </Button>
                              <IconButton size="small" onClick={(event) => handleRowMenuOpen(event, submission.Id)}><MoreVert /></IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Menu anchorEl={rowMenuAnchorEl} open={Boolean(rowMenuAnchorEl)} onClose={handleRowMenuClose}>
                  {statusOptions.map((status) => (
                    <MenuItem key={status} onClick={() => handleStatusUpdate(status)}>Mover para: {status.charAt(0).toUpperCase() + status.slice(1)}</MenuItem>
                  ))}
                </Menu>

                <TablePagination
                  rowsPerPageOptions={[5, 10, 25]}
                  component="div"
                  count={totalRows}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`}
                />
              </>
            )}
          </Paper>
        </Container>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </AppTheme>
  );
};

export default DashboardPage;